// src/components/dashboard/EapEntryChip.jsx
//
// The provider's mark, on the dashboard itself, before anything is clicked.
// Inside the support modal the logo only pays off for members who already went
// looking; out here it does the recognising for them — "that's the thing on my
// benefits card" — which is the whole reason brokers care about this surface.
//
// Sits on a dark brand band, so the logo gets a white tile of its own. Falls
// back to an icon + the provider's name when there's no logo or the image
// fails: the row must never render an empty hole.

import React, { useState } from 'react';
import { LifeBuoy, ChevronRight } from 'lucide-react';
import { availabilityLabel } from '../../utils/eapProgram';

const EapEntryChip = ({ eap, onOpen, className = '', ctaLabel = 'Talk to someone' }) => {
    const [failed, setFailed] = useState(false);
    if (!eap) return null;

    const showLogo = !!eap.logoUrl && !failed;
    const label = eap.programLabel || 'Employee & Family Assistance Program';
    // Availability is whatever the org actually configured — never an assumed
    // "24/7" just because a phone number exists.
    const subParts = [
        !showLogo && eap.providerName,
        'Free & confidential',
        availabilityLabel(eap)
    ].filter(Boolean);

    return (
        <button
            type="button"
            onClick={onOpen}
            className={`group inline-flex items-center gap-3 max-w-full p-2.5 pr-3.5 rounded-2xl bg-white/15 hover:bg-white/25 border border-white/30 text-left transition-colors ${className}`}
        >
            <span className="h-12 w-[4.5rem] rounded-xl bg-white flex items-center justify-center shrink-0 px-1.5">
                {showLogo ? (
                    <img
                        src={eap.logoUrl}
                        alt={eap.providerName || 'Assistance program provider'}
                        onError={() => setFailed(true)}
                        className="max-h-9 max-w-full object-contain"
                    />
                ) : (
                    <LifeBuoy size={22} className="text-brand-primary" />
                )}
            </span>
            <span className="min-w-0">
                {/* This chip IS the call to action now — the generic button that used
                    to sit beside it opened the very same modal. */}
                <span className="block text-[10px] font-black uppercase tracking-widest text-white/60">{ctaLabel}</span>
                <span className="block text-sm font-black text-white truncate">{label}</span>
                <span className="block text-[11px] font-bold text-white/60 truncate">{subParts.join(' · ')}</span>
            </span>
            <ChevronRight size={18} className="ml-auto shrink-0 text-white/50 group-hover:text-white transition-colors" />
        </button>
    );
};

export default EapEntryChip;
